import React, { useState, useEffect, useCallback, useRef } from 'react';
import SALocations from './SALocations';
import SAEmployeeDetail from './SAEmployeeDetail';
import './SuperAdmin.css';

const BASE = 'https://fotileams.com/api';

function getToken() { return localStorage.getItem('super_token'); }
function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getToken()}`,
  };
}

function formatPKR(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return `PKR ${(n/1000000).toFixed(1)}M`;
  if (n >= 1000)    return `PKR ${Math.round(n/1000)}K`;
  return `PKR ${Number(n).toLocaleString('en-PK')}`;
}

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getInitials(name) {
  if (!name) return '?';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length-1]?.[0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316'];
  let h = 0;
  for (let i = 0; i < (name||'').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

// ── LOGIN ─────────────────────────────────────────────────
function SuperLogin({ onLogin }) {
  const [id, setId]           = useState('');
  const [pw, setPw]           = useState('');
  const [err, setErr]         = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!id || !pw) { setErr('Enter credentials'); return; }
    setLoading(true); setErr('');
    try {
      const res  = await fetch(`${BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_id: id, password: pw }),
      });
      const data = await res.json();
      if (!res.ok || data.employee?.role !== 'super_admin') {
        setErr('Access denied'); return;
      }
      localStorage.setItem('super_token', data.token);
      localStorage.setItem('super_employee', JSON.stringify(data.employee));
      onLogin(data.employee);
    } catch { setErr('Connection error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="sl-root">
      <div className="sl-box">
        <div className="sl-icon">⬡</div>
        <div className="sl-title">Restricted Access</div>
        <div className="sl-sub">Authorized personnel only</div>
        {err && <div className="sl-err">{err}</div>}
        <input className="sl-input" type="text" placeholder="Access ID"
          value={id} onChange={e => setId(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && handleLogin()} />
        <input className="sl-input" type="password" placeholder="Access Code"
          value={pw} onChange={e => setPw(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleLogin()} />
        <button className="sl-btn" onClick={handleLogin} disabled={loading}>
          {loading ? '...' : 'Enter'}
        </button>
      </div>
    </div>
  );
}

// ── LIVE OPS ──────────────────────────────────────────────
function LiveOps({ onBack }) {
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [ticker, setTicker]       = useState([]);
  const [time, setTime]           = useState('');
  const [cityFilter, setCityFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const tickerRef                 = useRef([]);

  useEffect(() => {
    const t = setInterval(() => {
      setTime(new Date().toLocaleTimeString('en-PK', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
      }));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const fetchLive = useCallback(async () => {
    try {
      const [empRes, attRes] = await Promise.all([
        fetch(`${BASE}/employees/`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${BASE}/attendance/`, { headers: authHeaders() }).then(r => r.json()),
      ]);
      const emps = (empRes.employees || []).filter(e => e.role !== 'super_admin');
      const recs = attRes.records || [];
      setEmployees(emps);
      setAttendance(recs);
      const newEvents = recs.filter(r => r.clock_in).map(r => {
        const late = r.late_minutes > 0 ? ` · ${r.late_minutes}m late` : ' · On time';
        return `${r.full_name || r.emp_code} clocked in at ${formatTime(r.clock_in)}${late}`;
      });
      tickerRef.current = [...newEvents, ...tickerRef.current].slice(0, 20);
      setTicker([...tickerRef.current]);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    fetchLive();
    const t = setInterval(fetchLive, 30000);
    return () => clearInterval(t);
  }, [fetchLive]);

  const empCards = employees.map(emp => {
    const rec    = attendance.find(r => r.emp_code === emp.employee_id);
    const isIn   = rec?.clock_in && !rec?.clock_out;
    const isDone = rec?.clock_in && rec?.clock_out;
    const isLate = rec?.late_minutes > 0;
    let status   = 'absent';
    // Someone on their weekly off day is not absent — they're legitimately off.
    if (rec?.status === 'weekly_off')      status = 'off';
    else if (rec?.status === 'public_holiday') status = 'off';
    else if (isIn && isLate) status = 'late';
    else if (isIn)          status = 'in';
    else if (isDone)        status = 'done';
    return { ...emp, status, rec };
  });

  const total   = empCards.length;
  const inCount = empCards.filter(e => e.status === 'in').length;
  const lateCount = empCards.filter(e => e.status === 'late').length;
  const doneCount = empCards.filter(e => e.status === 'done').length;
  const absentCount = empCards.filter(e => e.status === 'absent').length;
  const offCount    = empCards.filter(e => e.status === 'off').length;
  const presentCount = inCount + lateCount + doneCount;
  const expected     = total - offCount;
  const rate = expected > 0 ? Math.round((presentCount / expected) * 100) : 0;

  const cities = ['Lahore','Karachi','Islamabad','Multan'];
  const cityStats = cities.map(city => ({
    city,
    total:   empCards.filter(e => e.city === city).length,
    present: empCards.filter(e => e.city === city && ['in','late','done'].includes(e.status)).length,
  })).filter(c => c.total > 0);

  // Apply filters to the card grid — the stat bar above always shows company totals
  const visibleCards = empCards.filter(e => {
    const matchCity   = cityFilter === 'All' || e.city === cityFilter;
    const matchStatus = statusFilter === 'All' || e.status === statusFilter;
    return matchCity && matchStatus;
  });

  const now = new Date();

  return (
    <div className="lo-root">
      <div className="lo-topbar">
        <div className="lo-topbar-left">
          <div className="lo-logo">⬡ FOTILE AMS</div>
          <div className="lo-subtitle">LIVE OPERATIONS CENTER</div>
        </div>
        <div className="lo-clock-wrap">
          <div className="lo-clock">{time}</div>
          <div className="lo-date">{now.toLocaleDateString('en-PK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
        </div>
        <div className="lo-topbar-right">
          <div className="lo-live-badge">● LIVE</div>
          <button className="lo-back-btn" onClick={onBack}>← Back</button>
        </div>
      </div>

      <div className="lo-stats-bar">
        <div className="lo-stat-big lo-s-blue">
          <div className="lo-stat-num">{total}</div>
          <div className="lo-stat-lbl">TOTAL</div>
        </div>
        <div className="lo-stat-big lo-s-green">
          <div className="lo-stat-num">{inCount}</div>
          <div className="lo-stat-lbl">CLOCKED IN</div>
        </div>
        <div className="lo-stat-big lo-s-amber">
          <div className="lo-stat-num">{lateCount}</div>
          <div className="lo-stat-lbl">LATE</div>
        </div>
        <div className="lo-stat-big lo-s-purple">
          <div className="lo-stat-num">{doneCount}</div>
          <div className="lo-stat-lbl">COMPLETED</div>
        </div>
        <div className="lo-stat-big lo-s-red">
          <div className="lo-stat-num">{absentCount}</div>
          <div className="lo-stat-lbl">ABSENT</div>
        </div>
        <div className="lo-stat-big lo-s-slate">
          <div className="lo-stat-num">{offCount}</div>
          <div className="lo-stat-lbl">WEEKLY OFF</div>
        </div>
        <div className="lo-rate-circle">
          <svg width="110" height="110" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="10"/>
            <circle cx="60" cy="60" r="50" fill="none"
              stroke={rate >= 80 ? '#22c55e' : rate >= 60 ? '#f59e0b' : '#ef4444'}
              strokeWidth="10"
              strokeDasharray={`${2*Math.PI*50}`}
              strokeDashoffset={`${2*Math.PI*50*(1-rate/100)}`}
              strokeLinecap="round" transform="rotate(-90 60 60)"
            />
            <text x="60" y="55" textAnchor="middle" fontSize="22" fontWeight="900" fill="#fff">{rate}%</text>
            <text x="60" y="72" textAnchor="middle" fontSize="10" fill="rgba(255,255,255,0.4)">RATE</text>
          </svg>
        </div>
      </div>

      <div className="lo-city-strip">
        <button className={`lo-city-pill lo-pill-btn ${cityFilter === 'All' ? 'lo-pill-on' : ''}`}
          onClick={() => setCityFilter('All')}>
          <span className="lo-city-name">All Cities</span>
          <span className="lo-city-count">{empCards.length}</span>
        </button>
        {cityStats.map(c => (
          <button key={c.city}
            className={`lo-city-pill lo-pill-btn ${cityFilter === c.city ? 'lo-pill-on' : ''}`}
            onClick={() => setCityFilter(c.city)}>
            <span className="lo-city-name">{c.city}</span>
            <span className="lo-city-count">{c.present}/{c.total}</span>
          </button>
        ))}
        <div className="lo-status-filters">
          {['All','in','late','done','absent','off'].map(s => (
            <button key={s}
              className={`lo-status-btn ${statusFilter === s ? 'lo-status-on' : ''}`}
              onClick={() => setStatusFilter(s)}>
              {s === 'All' ? 'All' : s === 'in' ? '● In' : s === 'late' ? '⏱ Late' : s === 'done' ? '✓ Done' : s === 'absent' ? '✗ Absent' : '🌙 Off'}
            </button>
          ))}
        </div>
      </div>

      <div className="lo-grid">
        {visibleCards.length === 0 && (
          <div className="lo-no-match">No employees match these filters</div>
        )}
        {visibleCards.map((emp, i) => (
          <div key={i} className={`lo-card lo-card-${emp.status}`}>
            <div className="lo-card-avatar" style={{ background: getAvatarColor(emp.full_name) }}>
              {getInitials(emp.full_name)}
            </div>
            <div className="lo-card-info">
              <div className="lo-card-name">{emp.full_name}</div>
              <div className="lo-card-id">{emp.employee_id} · {emp.department || '—'}</div>
              <div className="lo-card-city">{emp.city || '—'}</div>
            </div>
            <div className={`lo-card-status lo-cs-${emp.status}`}>
              {emp.status === 'in'     ? `IN ${formatTime(emp.rec?.clock_in)}` :
               emp.status === 'late'   ? `LATE +${emp.rec?.late_minutes}m` :
               emp.status === 'done'   ? `DONE ${formatTime(emp.rec?.clock_out)}` :
               emp.status === 'off'    ? '🌙 WEEKLY OFF' : 'ABSENT'}
            </div>
          </div>
        ))}
      </div>

      <div className="lo-ticker-wrap">
        <span className="lo-ticker-label">● LIVE FEED</span>
        <div className="lo-ticker-track">
          <div className="lo-ticker-content">
            {ticker.length > 0 ? ticker.join('   •   ') : 'No activity recorded today yet'}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── EMPLOYEES SECTION ─────────────────────────────────────
const CITIES = ['All Cities', 'Karachi', 'Lahore', 'Islamabad', 'Multan'];

function SAEmployees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [cityFilter, setCityFilter] = useState('All Cities');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive
  const [selected, setSelected]   = useState(null);
  const [resetPw, setResetPw]     = useState(null);
  const [newPw, setNewPw]         = useState('');
  const [pwMsg, setPwMsg]         = useState('');

  useEffect(() => { fetchEmployees(); }, [statusFilter]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      // include_inactive lets the Boss see former employees too
      const url = statusFilter === 'all' || statusFilter === 'inactive'
        ? `${BASE}/employees/?include_inactive=true`
        : `${BASE}/employees/`;
      const res  = await fetch(url, { headers: authHeaders() });
      const data = await res.json();
      setEmployees((data.employees || []).filter(e => e.role !== 'super_admin'));
    } catch {} finally { setLoading(false); }
  };

  // FIXED: PUT /employees/:id does not accept is_active. Deactivate and
  // reactivate have their own dedicated endpoints. The old code silently
  // did nothing.
  const toggleActive = async (emp) => {
    const action = emp.is_active ? 'deactivate' : 'reactivate';
    if (!window.confirm(`${emp.is_active ? 'Deactivate' : 'Reactivate'} ${emp.full_name}?\n\n${emp.is_active ? 'They will stop appearing in headcounts, attendance and reports. Their history is preserved.' : 'They will appear in the system again.'}`)) return;
    try {
      if (emp.is_active) {
        await fetch(`${BASE}/employees/${emp.id}`, { method: 'DELETE', headers: authHeaders() });
      } else {
        await fetch(`${BASE}/employees/${emp.id}/reactivate`, { method: 'POST', headers: authHeaders() });
      }
      fetchEmployees();
    } catch {}
  };

  const resetPassword = async (emp) => {
    if (!newPw || newPw.length < 6) { setPwMsg('Min 6 characters'); return; }
    try {
      const res  = await fetch(`${BASE}/employees/${emp.id}/reset-password`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ new_password: newPw }),
      });
      const data = await res.json();
      if (res.ok) { setPwMsg('Password reset successfully!'); setNewPw(''); setTimeout(() => { setResetPw(null); setPwMsg(''); }, 2000); }
      else { setPwMsg(data.error || 'Failed'); }
    } catch { setPwMsg('Error resetting password'); }
  };

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      e.full_name?.toLowerCase().includes(q) ||
      e.employee_id?.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q);
    const matchCity = cityFilter === 'All Cities' || e.city === cityFilter;
    const matchStatus =
      statusFilter === 'all' ? true :
      statusFilter === 'active' ? e.is_active : !e.is_active;
    return matchSearch && matchCity && matchStatus;
  });

  const activeCount   = employees.filter(e => e.is_active).length;
  const inactiveCount = employees.filter(e => !e.is_active).length;

  return (
    <div className="sa-section">
      <div className="sa-section-header">
        <h2 className="sa-section-title">All Employees</h2>
        <input className="sa-search" placeholder="Search name, ID, department…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="sa-filter-bar">
        <div className="sa-filter-group">
          {CITIES.map(c => (
            <button key={c} className={`sa-filter-btn ${cityFilter === c ? 'sa-filter-on' : ''}`}
              onClick={() => setCityFilter(c)}>{c}</button>
          ))}
        </div>
        <div className="sa-filter-group">
          {[
            { k: 'all',      l: `All (${employees.length})` },
            { k: 'active',   l: `Active (${activeCount})` },
            { k: 'inactive', l: `Former (${inactiveCount})` },
          ].map(f => (
            <button key={f.k} className={`sa-filter-btn ${statusFilter === f.k ? 'sa-filter-on' : ''}`}
              onClick={() => setStatusFilter(f.k)}>{f.l}</button>
          ))}
        </div>
      </div>

      {loading ? <div className="sa-loading">Loading…</div> : filtered.length === 0 ? (
        <div className="sa-empty">No employees match these filters</div>
      ) : (
        <div className="sa-emp-table">
          <div className="sa-table-head">
            <span>Employee</span>
            <span>Department</span>
            <span>Location</span>
            <span>Salary</span>
            <span>Status</span>
            <span>Actions</span>
          </div>
          {filtered.map((emp, i) => (
            <div key={i} className={`sa-table-row ${!emp.is_active ? 'sa-row-inactive' : ''}`}>
              <div className="sa-emp-cell sa-clickable" onClick={() => setSelected(emp)}>
                <div className="sa-emp-av" style={{ background: getAvatarColor(emp.full_name) }}>
                  {getInitials(emp.full_name)}
                </div>
                <div>
                  <div className="sa-emp-name">{emp.full_name}</div>
                  <div className="sa-emp-id">{emp.employee_id}</div>
                </div>
              </div>
              <span className="sa-td">{emp.department || '—'}</span>
              <span className="sa-td">{emp.city || '—'}</span>
              <span className="sa-td sa-salary">{formatPKR(emp.base_salary)}</span>
              <span className={`sa-td sa-status-badge ${emp.is_active ? 'sa-active' : 'sa-blocked'}`}>
                {emp.is_active ? 'Active' : 'Former'}
              </span>
              <div className="sa-td sa-actions">
                <button className="sa-action-btn sa-btn-view" onClick={() => setSelected(emp)}>
                  👁 View
                </button>
                <button className="sa-action-btn sa-btn-pw" onClick={() => { setResetPw(emp); setPwMsg(''); setNewPw(''); }}>
                  🔑 Reset
                </button>
                <button className={`sa-action-btn ${emp.is_active ? 'sa-btn-block' : 'sa-btn-unblock'}`}
                  onClick={() => toggleActive(emp)}>
                  {emp.is_active ? 'Deactivate' : 'Reactivate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && <SAEmployeeDetail employee={selected} onClose={() => setSelected(null)} />}

      {resetPw && (
        <div className="sa-modal-overlay" onClick={() => setResetPw(null)}>
          <div className="sa-modal" onClick={e => e.stopPropagation()}>
            <h3 className="sa-modal-title">Reset Password — {resetPw.full_name}</h3>
            <input className="sa-modal-input" type="password" placeholder="New password (min 6 chars)"
              value={newPw} onChange={e => setNewPw(e.target.value)} />
            {pwMsg && <div className={`sa-modal-msg ${pwMsg.includes('success') ? 'sa-msg-ok' : 'sa-msg-err'}`}>{pwMsg}</div>}
            <div className="sa-modal-btns">
              <button className="sa-modal-cancel" onClick={() => setResetPw(null)}>Cancel</button>
              <button className="sa-modal-confirm" onClick={() => resetPassword(resetPw)}>Reset Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── SALARY SECTION ────────────────────────────────────────
function SASalary() {
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear]   = useState(now.getFullYear());

  useEffect(() => { fetchSalary(); }, [month, year]);

  const fetchSalary = async () => {
    try {
      setLoading(true);
      const res  = await fetch(`${BASE}/salary/reports?month=${month}&year=${year}`, { headers: authHeaders() });
      const data = await res.json();
      setReports(data.reports || []);
      setSummary(data.summary || null);
    } catch {} finally { setLoading(false); }
  };

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div className="sa-section">
      <div className="sa-section-header">
        <h2 className="sa-section-title">Salary Reports</h2>
        <div className="sa-period-pills">
          {MONTHS.map((m, i) => (
            <button key={i} className={`sa-period-pill ${month === i+1 ? 'sa-pill-active' : ''}`}
              onClick={() => setMonth(i+1)}>{m}</button>
          ))}
        </div>
      </div>

      {summary && (
        <div className="sa-salary-summary">
          <div className="sa-sal-kpi sa-kpi-purple">
            <div className="sa-sal-kpi-val">{summary.avg_deduction_percent}%</div>
            <div className="sa-sal-kpi-lbl">Avg Deduction</div>
          </div>
          <div className="sa-sal-kpi">
            <div className="sa-sal-kpi-val">{formatPKR(summary.total_gross_salary)}</div>
            <div className="sa-sal-kpi-lbl">Gross Payroll ({summary.employees_with_salary} set)</div>
          </div>
          <div className="sa-sal-kpi sa-kpi-red">
            <div className="sa-sal-kpi-val">{formatPKR(summary.total_deductions)}</div>
            <div className="sa-sal-kpi-lbl">Total Deductions</div>
          </div>
          <div className="sa-sal-kpi sa-kpi-green">
            <div className="sa-sal-kpi-val">{formatPKR(summary.total_net_salary)}</div>
            <div className="sa-sal-kpi-lbl">Net Payroll</div>
          </div>
          <div className="sa-sal-kpi">
            <div className="sa-sal-kpi-val">{summary.total_employees}</div>
            <div className="sa-sal-kpi-lbl">Employees</div>
          </div>
        </div>
      )}

      {loading ? <div className="sa-loading">Loading…</div> : (
        <div className="sa-emp-table">
          <div className="sa-table-head">
            <span>Employee</span>
            <span>Department</span>
            <span>Deduction %</span>
            <span>Base Salary</span>
            <span>Deduction</span>
            <span>Net Salary</span>
          </div>
          {reports.map((r, i) => (
            <div key={i} className="sa-table-row">
              <div className="sa-emp-cell">
                <div className="sa-emp-av" style={{ background: getAvatarColor(r.employee_name) }}>
                  {getInitials(r.employee_name)}
                </div>
                <div>
                  <div className="sa-emp-name">{r.employee_name}</div>
                  <div className="sa-emp-id">{r.emp_code}</div>
                </div>
              </div>
              <span className="sa-td">{r.department || '—'}</span>
              <span className={`sa-td sa-status-badge ${parseFloat(r.deduction_percent) > 10 ? 'sa-blocked' : parseFloat(r.deduction_percent) > 0 ? 'sa-warn' : 'sa-active'}`}>
                {r.deduction_percent}%
              </span>
              <span className="sa-td sa-salary">{r.has_salary ? formatPKR(r.base_salary) : '— not set'}</span>
              <span className="sa-td" style={{ color: r.has_salary && parseFloat(r.total_deduction) > 0 ? '#ef4444' : '#94a3b8' }}>
                {r.has_salary ? (parseFloat(r.total_deduction) > 0 ? `-${formatPKR(r.total_deduction)}` : '—') : '—'}
              </span>
              <span className="sa-td sa-salary sa-net">{r.has_salary ? formatPKR(r.net_salary) : '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── AUDIT LOG SECTION ─────────────────────────────────────
function SAAuditLog() {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res  = await fetch(`${BASE}/employees/`, { headers: authHeaders() });
      const empData = await res.json();
      // Use attendance data as a proxy for activity log
      const attRes = await fetch(`${BASE}/attendance/?date=${new Date().toISOString().split('T')[0]}`, { headers: authHeaders() });
      const attData = await attRes.json();
      setLogs(attData.records || []);
    } catch {} finally { setLoading(false); }
  };

  const ACTION_COLORS = {
    'CLOCK_IN':           '#22c55e',
    'CLOCK_OUT':          '#3b82f6',
    'CLOCKIN_BLOCKED':    '#ef4444',
    'LEAVE_APPROVED':     '#10b981',
    'LEAVE_REJECTED':     '#f59e0b',
    'ATTENDANCE_OVERRIDE':'#8b5cf6',
    'LOCATION_UPDATED':   '#06b6d4',
  };

  return (
    <div className="sa-section">
      <h2 className="sa-section-title">Today's Activity Log</h2>
      {loading ? <div className="sa-loading">Loading…</div> : (
        <div className="sa-audit-list">
          {logs.length === 0 ? (
            <div className="sa-empty">No activity recorded today</div>
          ) : logs.map((log, i) => (
            <div key={i} className="sa-audit-row">
              <div className="sa-audit-av" style={{ background: getAvatarColor(log.full_name) }}>
                {getInitials(log.full_name)}
              </div>
              <div className="sa-audit-info">
                <div className="sa-audit-name">{log.full_name}</div>
                <div className="sa-audit-detail">
                  {log.emp_code} · {log.department || '—'} · {log.city}
                </div>
              </div>
              <div className="sa-audit-times">
                <div className="sa-audit-in">
                  IN: <strong>{formatTime(log.clock_in)}</strong>
                </div>
                <div className="sa-audit-out">
                  OUT: <strong>{formatTime(log.clock_out)}</strong>
                </div>
              </div>
              <div className={`sa-audit-status ${log.late_minutes > 0 ? 'sa-audit-late' : 'sa-audit-ok'}`}>
                {log.late_minutes > 0 ? `Late ${log.late_minutes}m` : 'On Time'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── HR CONTROL ────────────────────────────────────────────
function HRControl() {
  const [hrList, setHrList]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchHR(); }, []);

  const fetchHR = async () => {
    try {
      setLoading(true);
      const res  = await fetch(`${BASE}/employees/?include_inactive=true`, { headers: authHeaders() });
      const data = await res.json();
      setHrList((data.employees || []).filter(e => e.role === 'hr' || e.role === 'admin'));
    } catch {} finally { setLoading(false); }
  };

  // FIXED: PUT /employees/:id ignores is_active — use the dedicated endpoints.
  const toggleHR = async (emp) => {
    if (!window.confirm(`${emp.is_active ? 'Deactivate' : 'Reactivate'} ${emp.full_name}?`)) return;
    try {
      if (emp.is_active) {
        await fetch(`${BASE}/employees/${emp.id}`, { method: 'DELETE', headers: authHeaders() });
      } else {
        await fetch(`${BASE}/employees/${emp.id}/reactivate`, { method: 'POST', headers: authHeaders() });
      }
      fetchHR();
    } catch {}
  };

  return (
    <div className="sa-section">
      <h2 className="sa-section-title">HR Account Control</h2>
      {loading ? <div className="sa-loading">Loading…</div> : (
        <div className="sa-hr-list">
          {hrList.length === 0 ? (
            <div className="sa-empty">No HR accounts found</div>
          ) : hrList.map((emp, i) => (
            <div key={i} className="sa-hr-card">
              <div className="sa-hr-av" style={{ background: getAvatarColor(emp.full_name) }}>
                {getInitials(emp.full_name)}
              </div>
              <div className="sa-hr-info">
                <div className="sa-hr-name">{emp.full_name}</div>
                <div className="sa-hr-id">{emp.employee_id} · {emp.role?.toUpperCase()}</div>
              </div>
              <div className={`sa-hr-status ${emp.is_active ? 'sa-active' : 'sa-blocked'}`}>
                {emp.is_active ? 'Active' : 'Blocked'}
              </div>
              <button className={`sa-toggle-btn ${emp.is_active ? 'sa-btn-block' : 'sa-btn-unblock'}`}
                onClick={() => toggleHR(emp)}>
                {emp.is_active ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── OVERVIEW ──────────────────────────────────────────────
function SAOverview() {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const now = new Date();
      const [empRes, attRes, salRes, alertRes] = await Promise.all([
        fetch(`${BASE}/employees/`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${BASE}/attendance/`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${BASE}/salary/reports?month=${now.getMonth()+1}&year=${now.getFullYear()}`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${BASE}/attendance/alerts`, { headers: authHeaders() }).then(r => r.json()),
      ]);
      setStats({ empRes, attRes, salRes, alertRes });
    } catch {} finally { setLoading(false); }
  };

  if (loading) return <div className="sa-loading">Loading…</div>;

  const employees   = (stats.empRes.employees || []).filter(e => e.role !== 'super_admin');
  const attendance  = stats.attRes.records || [];
  const salSummary  = stats.salRes.summary || {};
  const lateAlerts  = stats.alertRes.late_alerts || [];
  const absentToday = stats.alertRes.absent_today || [];

  // Only count people who genuinely clocked in — a 'weekly_off' or holiday
  // attendance row is not attendance. And measure against who was actually
  // EXPECTED in today (excludes staff on their location's weekly off day).
  const presentToday = attendance.filter(r => r.clock_in).length;
  const expectedToday = stats.attRes.summary?.expected_today ?? employees.length;
  const absentCount  = Math.max(0, expectedToday - presentToday);
  const rate         = expectedToday > 0 ? Math.round((presentToday / expectedToday) * 100) : 0;

  return (
    <div className="sa-section">
      <h2 className="sa-section-title">System Overview</h2>

      <div className="sa-ov-grid">
        <div className="sa-ov-card sa-ov-blue">
          <div className="sa-ov-num">{employees.length}</div>
          <div className="sa-ov-lbl">Total Employees</div>
        </div>
        <div className="sa-ov-card sa-ov-green">
          <div className="sa-ov-num">{presentToday}</div>
          <div className="sa-ov-lbl">Present Today</div>
        </div>
        <div className="sa-ov-card sa-ov-red">
          <div className="sa-ov-num">{absentCount}</div>
          <div className="sa-ov-lbl">Absent Today</div>
        </div>
        <div className="sa-ov-card sa-ov-purple">
          <div className="sa-ov-num">{rate}%</div>
          <div className="sa-ov-lbl">Attendance Rate</div>
        </div>
        <div className="sa-ov-card sa-ov-teal">
          <div className="sa-ov-num">{formatPKR(salSummary.total_net_salary)}</div>
          <div className="sa-ov-lbl">Net Payroll</div>
        </div>
        <div className="sa-ov-card sa-ov-red">
          <div className="sa-ov-num">{formatPKR(salSummary.total_deductions)}</div>
          <div className="sa-ov-lbl">Deductions</div>
        </div>
      </div>

      {lateAlerts.length > 0 && (
        <>
          <h3 className="sa-sub-title">⚠ Late Alert Employees ({lateAlerts.length})</h3>
          <div className="sa-alert-list">
            {lateAlerts.map((emp, i) => (
              <div key={i} className="sa-alert-row">
                <div className="sa-emp-av" style={{ background: getAvatarColor(emp.full_name) }}>
                  {getInitials(emp.full_name)}
                </div>
                <div className="sa-audit-info">
                  <div className="sa-emp-name">{emp.full_name}</div>
                  <div className="sa-emp-id">{emp.employee_id} · {emp.department} · {emp.city}</div>
                </div>
                <div className="sa-alert-badge">{emp.late_count} lates</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────
export default function SuperAdmin() {
  const [employee, setEmployee] = useState(() => {
    const saved = localStorage.getItem('super_employee');
    return saved ? JSON.parse(saved) : null;
  });
  const [page, setPage]       = useState('overview');
  const [liveMode, setLiveMode] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('super_token');
    localStorage.removeItem('super_employee');
    setEmployee(null);
  };

  if (!employee) return <SuperLogin onLogin={setEmployee} />;
  if (liveMode)  return <LiveOps onBack={() => setLiveMode(false)} />;

  const NAV = [
    { key: 'overview',   label: 'Overview',    icon: '📊' },
    { key: 'hr',         label: 'HR Control',  icon: '🔐' },
    { key: 'employees',  label: 'Employees',   icon: '👥' },
    { key: 'salary',     label: 'Salary',      icon: '💰' },
    { key: 'locations',  label: 'Locations',   icon: '📍' },
    { key: 'audit',      label: 'Audit Log',   icon: '📋' },
  ];

  return (
    <div className="sa-shell">
      <aside className="sa-sidebar">
        <div className="sa-brand">
          <div className="sa-brand-icon">⬡</div>
          <div>
            <div className="sa-brand-name">Super Admin</div>
            <div className="sa-brand-sub">Restricted Access</div>
          </div>
        </div>

        <button className="sa-live-btn" onClick={() => setLiveMode(true)}>
          ● Live Operations
        </button>

        <nav className="sa-nav">
          {NAV.map(n => (
            <button key={n.key}
              className={`sa-nav-item ${page === n.key ? 'sa-nav-active' : ''}`}
              onClick={() => setPage(n.key)}>
              <span>{n.icon}</span>
              <span>{n.label}</span>
            </button>
          ))}
        </nav>

        <div className="sa-footer">
          <div className="sa-user">{employee.full_name}</div>
          <button className="sa-logout" onClick={handleLogout}>Sign out</button>
        </div>
      </aside>

      <main className="sa-main">
        {page === 'overview'  && <SAOverview />}
        {page === 'hr'        && <HRControl />}
        {page === 'employees' && <SAEmployees />}
        {page === 'salary'    && <SASalary />}
        {page === 'audit'     && <SAAuditLog />}
        {page === 'locations'  && <SALocations />}
      </main>
    </div>
  );
}